import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { listCompanyUsers, getCalendar, getTimesheet } from "../api";
import { statusesAsList, countWorkDays } from "../constants";
import { computeStats, calcSalary } from "../controllers/salary";
import { computeTaxes, listTaxes } from "../controllers/taxes";

const MONTH_KEYS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const COLOR_PALETTE = ["#0d6efd", "#dc3545", "#ffc107", "#6f42c1", "#198754", "#fd7e14", "#20c997", "#e83e8c", "#6c757d", "#0dcaf0"];
const NET_COLOR = "#198754";

export default function TaxBurdenWidget({ user, company }) {
  const { t, i18n } = useTranslation();
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);
  const [aggregated, setAggregated] = useState({ months: [], totals: { gross: 0, net: 0, byKey: {}, employer_total: 0 } });

  const isPrivileged = user && (user.role === "director" || user.role === "hr");
  const localeMap = { ru: "ru-RU", kk: "kk-KZ", en: "en-US" };
  const numLocale = localeMap[i18n.language] || "ru-RU";
  const fmt = (n) => Math.round(Number(n) || 0).toLocaleString(numLocale);

  const cfg = company?.tax_config || {};
  const taxes = useMemo(() => listTaxes(cfg), [cfg]);
  const employeeTaxes = taxes.filter(x => x.apply_to !== "employer");
  const employerTaxes = taxes.filter(x => x.apply_to === "employer");
  const colorByKey = useMemo(() => {
    const m = {};
    taxes.forEach((x, i) => { m[x.key] = COLOR_PALETTE[i % COLOR_PALETTE.length]; });
    return m;
  }, [taxes]);
  const statuses = useMemo(() => statusesAsList(company), [company]);

  const computeMonth = (users, calendar, timesheet) => {
    const norm = countWorkDays(calendar.days);
    const acc = { gross: 0, net: 0, byKey: {}, employer_extras: 0 };
    for (const t of taxes) acc.byKey[t.key] = 0;
    for (const u of users) {
      const stats = computeStats(timesheet.entries, u.user_id, calendar.days, statuses);
      const { gross } = calcSalary({
        monthlySalary: u.salary || 0, normWorkDays: norm,
        paidDays: stats.paidDays, fixedTotal: stats.fixedTotal,
      });
      const tax = computeTaxes(gross, cfg);
      acc.gross += tax.gross;
      acc.net   += tax.net;
      for (const t of taxes) {
        const v = tax.byTax[t.key]?.amount || 0;
        acc.byKey[t.key] += v;
        if (t.apply_to === "employer") acc.employer_extras += v;
      }
    }
    acc.employer_total = acc.gross + acc.employer_extras;
    return acc;
  };

  useEffect(() => {
    if (!isPrivileged) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);

    const now = new Date();
    const Y = now.getFullYear();
    const Mcurr = now.getMonth();
    const range = period === "year" ? Array.from({ length: 12 }, (_, m) => m) : [Mcurr];

    (async () => {
      try {
        const users = await listCompanyUsers();
        const data = await Promise.all(range.map(async (m) => {
          const [cal, ts] = await Promise.all([getCalendar(Y, m), getTimesheet(Y, m)]);
          return { m, cal, ts };
        }));
        if (cancelled) return;
        const months = data.map(({ m, cal, ts }) => {
          const agg = computeMonth(users, cal, ts);
          // flatten byKey for recharts
          const flat = { m, label: t(`months.${MONTH_KEYS[m]}`).slice(0, 3),
            gross: agg.gross, net: agg.net, employer_total: agg.employer_total };
          for (const k of Object.keys(agg.byKey)) flat[`tx_${k}`] = agg.byKey[k];
          return flat;
        });
        const totals = months.reduce((acc, x) => {
          acc.gross += x.gross; acc.net += x.net; acc.employer_total += x.employer_total;
          for (const t of taxes) acc.byKey[t.key] = (acc.byKey[t.key] || 0) + (x[`tx_${t.key}`] || 0);
          return acc;
        }, { gross: 0, net: 0, byKey: {}, employer_total: 0 });
        setAggregated({ months, totals });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, isPrivileged, company?.company_id, taxes.length]);

  if (!isPrivileged) return null;

  const { totals, months } = aggregated;

  // Pie data — Net + each enabled tax
  const pieData = [
    { name: t("tax_burden.net_label"), value: Math.max(0, totals.net), key: "net", color: NET_COLOR },
    ...taxes.map(t => ({
      name: t.apply_to === "employer" ? `${t.label} *` : t.label,
      value: totals.byKey[t.key] || 0, key: t.key, color: colorByKey[t.key],
    })),
  ].filter(x => x.value > 0);

  const taxesTotal = Object.values(totals.byKey).reduce((s, v) => s + v, 0);
  const employerTotal = totals.employer_total;
  const burdenPct = employerTotal > 0 ? Math.round((taxesTotal / employerTotal) * 1000) / 10 : 0;

  const now = new Date();
  const monthsPassed = period === "year" ? Math.max(1, now.getMonth() + 1) : 1;
  const forecastYear = period === "year" ? (employerTotal / monthsPassed) * 12 : employerTotal * 12;
  const forecastTaxes = period === "year" ? (taxesTotal / monthsPassed) * 12 : taxesTotal * 12;

  const employerLabelsList = employerTaxes.map(x => x.label).join(", ");

  return (
    <div className="card card-soft mb-4" data-testid="tax-burden-widget"><div className="card-body">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h6 className="fw-bold mb-0">
          <i className="bi bi-pie-chart-fill me-2 text-primary"></i>
          {t("tax_burden.title")}
        </h6>
        <div className="btn-group btn-group-sm" role="group">
          <button className={`btn ${period === "month" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setPeriod("month")} data-testid="tb-period-month">
            {t("tax_burden.period_month")}
          </button>
          <button className={`btn ${period === "year" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setPeriod("year")} data-testid="tb-period-year">
            {t("tax_burden.period_year")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted py-4">
          <div className="spinner-border spinner-border-sm me-2"></div>
          {t("common.loading")}
        </div>
      ) : totals.gross === 0 ? (
        <div className="text-center text-muted py-4 small">{t("tax_burden.no_data")}</div>
      ) : (
        <>
          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <div className="p-3 rounded" style={{ background: "#e8f0ff" }}>
                <div className="small text-muted">{t("tax_burden.employer_total")}</div>
                <div className="fs-5 fw-bold" data-testid="tb-employer-total">{fmt(employerTotal)} ₸</div>
                <div className="small text-muted">{t("tax_burden.employer_hint")}</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-3 rounded" style={{ background: "#e7f7ec" }}>
                <div className="small text-muted">{t("tax_burden.employee_net")}</div>
                <div className="fs-5 fw-bold text-success" data-testid="tb-employee-net">{fmt(totals.net)} ₸</div>
                <div className="small text-muted">{t("tax_burden.employee_hint")}</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-3 rounded" style={{ background: "#fff4e0" }}>
                <div className="small text-muted">{t("tax_burden.taxes_total")}</div>
                <div className="fs-5 fw-bold text-danger" data-testid="tb-taxes-total">
                  {fmt(taxesTotal)} ₸
                  <span className="small text-muted ms-2">({burdenPct}%)</span>
                </div>
                <div className="small text-muted">{t("tax_burden.burden_hint")}</div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-5">
              <div className="text-muted small mb-1 fw-semibold">{t("tax_burden.distribution")}</div>
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name"
                         cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                      {pieData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${fmt(v)} ₸`} />
                    <Legend verticalAlign="bottom" height={36} iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="col-lg-7">
              <div className="text-muted small mb-1 fw-semibold">
                {period === "year" ? t("tax_burden.by_month") : t("tax_burden.breakdown")}
              </div>
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={months} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }}
                           tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                    <Tooltip formatter={(v) => `${fmt(v)} ₸`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                    <Bar dataKey="net" name={t("tax_burden.net_label")} stackId="a" fill={NET_COLOR} />
                    {taxes.map(x => (
                      <Bar key={x.key} dataKey={`tx_${x.key}`} name={x.label}
                           stackId="a" fill={colorByKey[x.key]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="mt-3 p-2 rounded" style={{ background: "#f6f8fb" }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 small">
              <div>
                <i className="bi bi-graph-up-arrow me-1 text-primary"></i>
                <strong>{t("tax_burden.forecast_label")}:</strong>
              </div>
              <div className="d-flex gap-3 flex-wrap">
                <span>{t("tax_burden.forecast_employer")}: <strong>{fmt(forecastYear)} ₸</strong></span>
                <span className="text-danger">{t("tax_burden.forecast_taxes")}: <strong>{fmt(forecastTaxes)} ₸</strong></span>
              </div>
            </div>
            {employerLabelsList && (
              <div className="text-muted" style={{ fontSize: 11 }}>
                {t("tax_burden.so_footnote", { label: employerLabelsList })}
              </div>
            )}
          </div>
        </>
      )}
    </div></div>
  );
}
